import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Camera, 
  Tag, 
  MessageSquare, 
  Mail, 
  Download, 
  Zap, 
  CheckCircle2, 
  ArrowRight,
  ShieldCheck,
  Smartphone,
  Sparkles,
  FileSpreadsheet,
  FileText,
  Clock,
  Check,
  TrendingUp,
  Award
} from 'lucide-react';

const Features = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  
  // Interactive states for the live card widgets
  const [selectedTags, setSelectedTags] = useState(['🔥 Hot Prospect', '⭐ Decision Maker']);
  const [exportFormat, setExportFormat] = useState('.XLSX');
  
  // Interactive state for the NEW left-column ROI Calculator
  const [leadCount, setLeadCount] = useState(350);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const featuresList = [
    {
      id: 1,
      number: "01",
      title: "Smart Business Card Scanner",
      subtitle: "Speed Matters at the Booth",
      description: "Capture visitor details instantly using our lightning-fast cloud character recognition. Automatic text extraction eliminates manual typing in busy exhibition environments where every second counts.",
      color: "bg-blue-600",
      lightColor: "bg-blue-50",
      borderColor: "border-blue-200",
      textColor: "text-blue-600",
      icon: <Camera className="w-6 h-6 text-white" />
    },
    {
      id: 2,
      number: "02",
      title: "Intelligent Lead Tagging",
      subtitle: "Segment & Prioritize Prospects",
      description: "A comprehensive lead tagging interface allows your sales team to categorize visitors by interest level, product line, and urgency. Ensure your CRM receives structured, actionable data.",
      color: "bg-indigo-600",
      lightColor: "bg-indigo-50",
      borderColor: "border-indigo-200",
      textColor: "text-indigo-600",
      icon: <Tag className="w-6 h-6 text-white" />
    },
    {
      id: 3,
      number: "03",
      title: "Instant WhatsApp Outreach",
      subtitle: "The 10-Second Engagement Rule",
      description: "Our signature feature triggers an automatic, customized WhatsApp greeting with your product brochure attached the moment a card is saved. Engage prospects before they even leave your booth.",
      color: "bg-emerald-600",
      lightColor: "bg-emerald-50",
      borderColor: "border-emerald-200",
      textColor: "text-emerald-600",
      icon: <MessageSquare className="w-6 h-6 text-white" />
    },
    {
      id: 4,
      number: "04",
      title: "Automated Email Follow-ups",
      subtitle: "Keep Your Brand Top of Mind",
      description: "Schedule automated email campaigns at intervals you choose to re-engage exhibition visitors. Your sales administrators are CC'd on every message to ensure seamless conversation handoffs.",
      color: "bg-purple-600",
      lightColor: "bg-purple-50",
      borderColor: "border-purple-200",
      textColor: "text-purple-600",
      icon: <Mail className="w-6 h-6 text-white" />
    },
    {
      id: 5,
      number: "05",
      title: "One-Click Export & CRM Sync",
      subtitle: "Easy Data Portability",
      description: "View all captured leads, visit locations, and tags in one unified cloud dashboard. Download your reports instantly as Excel/PDF files, or share lead lists directly with your team via WhatsApp.",
      color: "bg-amber-600",
      lightColor: "bg-amber-50",
      borderColor: "border-amber-200",
      textColor: "text-amber-600",
      icon: <Download className="w-6 h-6 text-white" />
    }
  ];

  return (
    <div className="flex flex-col min-h-screen font-sans text-gray-900 bg-slate-50 selection:bg-blue-200 overflow-x-hidden relative">
      
      {/* 1. SHARED HEADER (Permanently Visible Get Started Button + Clean Nav) */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${
          isScrolled 
            ? "bg-white/95 backdrop-blur-md border-gray-200 py-3.5 shadow-sm" 
            : "bg-white/80 backdrop-blur-sm border-gray-100 py-5"
        }`}
      >
        <div className="flex items-center justify-between w-full max-w-7xl px-6 mx-auto">
          <Link to="/" className="text-2xl font-black text-gray-900 tracking-tighter">
            Expo<span className="text-blue-600">Lead</span>
          </Link>

          {/* Clean Center Navigation */}
          <nav className="hidden md:flex items-center gap-8 font-semibold text-gray-600">
            <Link to="/" className="hover:text-blue-600 transition">Home</Link>
            <Link to="/features" className="text-blue-600 font-bold flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"></span>
              Features
            </Link>
            <Link to="/about" className="hover:text-blue-600 transition">
              About Us
            </Link>
          </nav>

          {/* Right CTA - PERMANENTLY VISIBLE BUTTON */}
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate("/auth/login")} 
              className="text-sm font-semibold text-gray-600 transition hover:text-blue-600"
            >
              Log in
            </button>
            <button 
              onClick={() => navigate("/auth/register")} 
              className="px-6 py-2.5 text-sm font-bold text-white bg-blue-600 rounded-full hover:bg-blue-700 active:scale-95 transition-all shadow-lg shadow-blue-600/30 opacity-100 block"
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* 2. HERO BANNER */}
      <main className="flex-grow pt-28 pb-20">
        
        {/* Background Glowing Orbs */}
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-full max-w-5xl h-[350px] bg-gradient-to-tr from-blue-400/20 via-indigo-500/15 to-purple-500/20 blur-[120px] rounded-full pointer-events-none -z-10"></div>

        <div className="max-w-7xl mx-auto px-6 text-center mb-16 sm:mb-24">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-100/80 text-blue-800 border border-blue-200 mb-6 shadow-sm">
            <Zap className="w-3.5 h-3.5 text-blue-600 fill-current" />
            The Complete Exhibition Toolkit
          </div>
          
          {/* TITLE BLOCK WITH TOP-LEFT APP LOGO */}
          <div className="relative flex flex-col md:flex-row items-center justify-center gap-4 max-w-5xl mx-auto mb-6">
            <img 
              src="/logo.png" 
              alt="ExpoLead Logo" 
              className="w-16 h-16 sm:w-20 sm:h-20 object-contain rounded-2xl shadow-md md:absolute md:-left-8 md:top-0 lg:left-0" 
            />
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight text-gray-900 uppercase leading-none">
              TRANSFORM YOUR EXHIBITION EXPERIENCE WITH <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-600">EXPOLEAD</span>
            </h1>
          </div>
          
          <p className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto font-normal">
            Designed specifically for high-speed B2B trade shows. Discover how our 5-stage automated lead funnel eliminates manual data entry and supercharges your sales conversions.
          </p>

          {/* SLOGAN BANNER */}
          <div className="mt-8 flex justify-center">
            <div className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-black font-black text-xs sm:text-sm tracking-widest uppercase shadow-lg shadow-blue-500/20 ring-4 ring-blue-500/10">
              🤳 NEVER LOSE A LEAD AGAIN
            </div>
          </div>
        </div>

        {/* 3. INTERACTIVE FUNNEL & STAGGERED CARDS SECTION */}
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {/* LEFT COLUMN: Sticky Funnel Graphic + NEW Interactive ROI Widget */}
            <div className="lg:col-span-5 lg:sticky lg:top-28 flex flex-col items-center gap-6">
              
              {/* CARD 1: The Funnel Navigation */}
              <div className="w-full max-w-md bg-slate-900 rounded-[3rem] p-8 shadow-2xl border-4 border-slate-800 relative overflow-hidden">
                <div className="absolute -top-24 -left-24 w-60 h-60 bg-blue-500/20 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-24 -right-24 w-60 h-60 bg-emerald-500/20 rounded-full blur-3xl"></div>
                
                <div className="text-center mb-8 relative z-10">
                  <span className="text-xs font-bold tracking-widest text-slate-400 uppercase">Interactive Funnel</span>
                  <h3 className="text-xl font-black text-white mt-1">The 5-Stage Lead Engine</h3>
                </div>

                {/* Stacked Layer Cards */}
                <div className="space-y-3 relative z-10">
                  {featuresList.map((item) => (
                    <div 
                      key={item.id}
                      onClick={() => {
                        setActiveStep(item.id);
                        document.getElementById(`feature-${item.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                      className={`flex items-center gap-4 p-4 rounded-2xl cursor-pointer transition-all duration-300 ${
                        activeStep === item.id 
                          ? 'bg-white/15 border border-white/30 translate-x-2 shadow-lg' 
                          : 'bg-white/5 border border-transparent hover:bg-white/10 opacity-70 hover:opacity-100'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center flex-shrink-0 shadow-md`}>
                        {item.icon}
                      </div>
                      <div className="flex-1 text-left">
                        <div className="text-xs font-bold text-slate-400">STAGE {item.number}</div>
                        <div className="text-base font-bold text-white leading-tight">{item.title}</div>
                      </div>
                      <ArrowRight className={`w-5 h-5 text-white/50 transition-transform ${activeStep === item.id ? 'translate-x-1 text-white' : ''}`} />
                    </div>
                  ))}
                </div>

                <div className="mt-8 pt-6 border-t border-slate-800/80 flex items-center justify-between text-xs font-semibold text-slate-400">
                  <span className="flex items-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-emerald-400" /> Bank-Grade Cloud Security
                  </span>
                  <span>24/7 Reliability</span>
                </div>
              </div>

              {/* CARD 2 (NEW!): Interactive ROI & Time-Saved Calculator (Fills the bottom left space) */}
              <div className="w-full max-w-md bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 text-white rounded-[2.5rem] p-7 shadow-xl border border-white/20 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>
                
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-300" />
                    <span className="text-xs font-bold uppercase tracking-wider text-blue-100">Live ROI Estimator</span>
                  </div>
                  <span className="px-2.5 py-1 rounded-full bg-white/20 text-[10px] font-bold uppercase tracking-wider">Interactive</span>
                </div>

                <h4 className="text-lg font-black mb-1">Calculate Your Show Savings</h4>
                <p className="text-xs text-blue-100 mb-5">Slide to set how many business cards your team expects to collect at your next exhibition:</p>

                {/* Slider Control */}
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/15 mb-5">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-bold text-blue-200">Expected Show Leads:</span>
                    <span className="text-xl font-black text-white bg-blue-500/50 px-3 py-0.5 rounded-xl border border-white/20">{leadCount} cards</span>
                  </div>
                  <input 
                    type="range" 
                    min="50" 
                    max="1000" 
                    step="25"
                    value={leadCount}
                    onChange={(e) => setLeadCount(Number(e.target.value))}
                    className="w-full h-2 bg-blue-950/60 rounded-lg appearance-none cursor-pointer accent-emerald-400"
                  />
                  <div className="flex justify-between text-[10px] font-semibold text-blue-200 mt-1">
                    <span>50</span>
                    <span>500</span>
                    <span>1,000+</span>
                  </div>
                </div>

                {/* Dynamic Results Grid */}
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="bg-slate-900/40 rounded-2xl p-3 border border-white/10">
                    <span className="block text-[10px] font-bold text-blue-200 uppercase">Manual Typing Saved</span>
                    <span className="text-2xl font-black text-emerald-300">~{Math.round(leadCount * 5 / 60)} hrs</span>
                    <span className="block text-[9px] text-slate-300 mt-0.5">at 5 mins per card</span>
                  </div>
                  <div className="bg-slate-900/40 rounded-2xl p-3 border border-white/10">
                    <span className="block text-[10px] font-bold text-blue-200 uppercase">Follow-up Speed</span>
                    <span className="text-2xl font-black text-cyan-300">Instant</span>
                    <span className="block text-[9px] text-slate-300 mt-0.5">vs 3 days avg delay</span>
                  </div>
                </div>

                <div className="mt-4 text-center">
                  <span className="text-[11px] font-semibold text-blue-100 flex items-center justify-center gap-1">
                    <Award className="w-3.5 h-3.5 text-amber-300" /> Boost your sales conversion rate by up to 40%
                  </span>
                </div>
              </div>

            </div>

            {/* RIGHT COLUMN: Highly Interactive & User-Friendly Feature Cards */}
            <div className="lg:col-span-7 space-y-8 sm:space-y-12">
              
              {/* CARD 01: SMART OCR */}
              <div 
                id="feature-1"
                onMouseEnter={() => setActiveStep(1)}
                className={`p-8 sm:p-10 rounded-[2.5rem] bg-white border transition-all duration-300 shadow-sm hover:shadow-xl relative overflow-hidden group ${
                  activeStep === 1 ? 'border-blue-300 ring-4 ring-blue-500/5 shadow-lg' : 'border-gray-200/80'
                }`}
              >
                <div className="absolute top-0 left-0 right-0 h-2 bg-blue-600"></div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-blue-600 text-white font-black text-2xl flex items-center justify-center shadow-lg transform group-hover:scale-105 transition-transform flex-shrink-0">01</div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-blue-50 text-blue-600 border border-blue-200">Speed Matters at the Booth</span>
                    <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mt-2">Smart Business Card Scanner</h2>
                  </div>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed pl-0 sm:pl-20">
                  Capture visitor details instantly using our lightning-fast cloud character recognition. Automatic text extraction eliminates manual typing in busy exhibition environments where every second counts.
                </p>

                {/* LIVE INTERACTIVE WIDGET 1: User-Friendly Processing Bar */}
                <div className="mt-6 sm:ml-20 p-4 rounded-2xl bg-slate-900 text-white border border-slate-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-inner">
                  <div className="flex items-center gap-3.5">
                    <div className="w-11 h-11 rounded-xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center text-blue-400 font-mono font-bold text-xs shadow-sm">
                      <Sparkles className="w-5 h-5 animate-spin" style={{ animationDuration: '4s' }} />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-200 flex items-center gap-2">
                        <span>Alex Morgan • VP of Sales</span>
                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping"></span>
                      </div>
                      <div className="text-[11px] text-emerald-400 font-medium mt-0.5">✓ 99.8% Recognition Accuracy Rate</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end sm:self-auto">
                    <span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold">Speed:</span>
                    <span className="px-3 py-1 rounded-lg bg-blue-600/30 border border-blue-500/30 text-blue-300 text-xs font-bold animate-pulse">0.2 Seconds</span>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between pl-0 sm:pl-20 text-sm font-bold text-gray-500">
                  <span className="flex items-center gap-2 text-gray-700"><CheckCircle2 className="w-5 h-5 text-blue-600" /> Verified for High-Density Exhibitions</span>
                  <button onClick={() => navigate("/auth/register")} className="hidden sm:inline-flex items-center gap-1 font-bold text-blue-600 hover:underline">Try Feature &rarr;</button>
                </div>
              </div>

              {/* CARD 02: INTELLIGENT LEAD TAGGING */}
              <div 
                id="feature-2"
                onMouseEnter={() => setActiveStep(2)}
                className={`p-8 sm:p-10 rounded-[2.5rem] bg-white border transition-all duration-300 shadow-sm hover:shadow-xl relative overflow-hidden group ${
                  activeStep === 2 ? 'border-indigo-300 ring-4 ring-indigo-500/5 shadow-lg' : 'border-gray-200/80'
                }`}
              >
                <div className="absolute top-0 left-0 right-0 h-2 bg-indigo-600"></div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-indigo-600 text-white font-black text-2xl flex items-center justify-center shadow-lg transform group-hover:scale-105 transition-transform flex-shrink-0">02</div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-200">Segment & Prioritize Prospects</span>
                    <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mt-2">Intelligent Lead Tagging</h2>
                  </div>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed pl-0 sm:pl-20">
                  A comprehensive lead tagging interface allows your sales team to categorize visitors by interest level, product line, and urgency. Ensure your CRM receives structured, actionable data.
                </p>

                {/* LIVE INTERACTIVE WIDGET 2: Clickable Tag Selector */}
                <div className="mt-6 sm:ml-20 p-5 rounded-2xl bg-indigo-50/70 border border-indigo-100/80">
                  <div className="flex items-center justify-between mb-2.5">
                    <span className="text-xs font-bold uppercase tracking-wider text-indigo-900">Interactive Demo — Click tags to test:</span>
                    <span className="text-[11px] font-semibold text-indigo-600">{selectedTags.length} selected</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {['🔥 Hot Prospect', '⭐ Decision Maker', '📦 Product Demo Req', '⚡ Immediate Call', '🤝 Partner Opportunity'].map((tag, idx) => {
                      const isSelected = selectedTags.includes(tag);
                      return (
                        <button 
                          key={idx} 
                          onClick={() => toggleTag(tag)}
                          className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 active:scale-95 ${
                            isSelected 
                              ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20 scale-105' 
                              : 'bg-white text-gray-700 border border-gray-200/80 hover:border-indigo-300'
                          }`}
                        >
                          {isSelected && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between pl-0 sm:pl-20 text-sm font-bold text-gray-500">
                  <span className="flex items-center gap-2 text-gray-700"><CheckCircle2 className="w-5 h-5 text-indigo-600" /> Verified for High-Density Exhibitions</span>
                  <button onClick={() => navigate("/auth/register")} className="hidden sm:inline-flex items-center gap-1 font-bold text-indigo-600 hover:underline">Try Feature &rarr;</button>
                </div>
              </div>

              {/* CARD 03: WHATSAPP OUTREACH */}
              <div 
                id="feature-3"
                onMouseEnter={() => setActiveStep(3)}
                className={`p-8 sm:p-10 rounded-[2.5rem] bg-white border transition-all duration-300 shadow-sm hover:shadow-xl relative overflow-hidden group ${
                  activeStep === 3 ? 'border-emerald-300 ring-4 ring-emerald-500/5 shadow-lg' : 'border-gray-200/80'
                }`}
              >
                <div className="absolute top-0 left-0 right-0 h-2 bg-emerald-600"></div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-600 text-white font-black text-2xl flex items-center justify-center shadow-lg transform group-hover:scale-105 transition-transform flex-shrink-0">03</div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">The 10-Second Engagement Rule</span>
                    <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mt-2">Instant WhatsApp Outreach</h2>
                  </div>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed pl-0 sm:pl-20">
                  Our signature feature triggers an automatic, customized WhatsApp greeting with your product brochure attached the moment a card is saved. Engage prospects before they even leave your booth.
                </p>

                {/* LIVE INTERACTIVE WIDGET 3: Simulated WhatsApp Chat Bubble */}
                <div className="mt-6 sm:ml-20 p-5 rounded-2xl bg-emerald-50/70 border border-emerald-100/80 space-y-3">
                  <div className="flex items-center justify-between text-xs font-bold text-emerald-900">
                    <span className="flex items-center gap-1.5"><Smartphone className="w-4 h-4 text-emerald-600" /> Instant Cloud Message Delivery</span>
                    <span className="px-2 py-0.5 rounded bg-emerald-200/60 text-emerald-800 text-[10px] font-bold">Sent in 4 seconds</span>
                  </div>
                  <div className="p-4 rounded-2xl rounded-tl-none bg-white border border-emerald-200/80 shadow-sm text-xs sm:text-sm text-gray-800 space-y-2">
                    <p className="font-semibold text-gray-900">👋 Hi David! Great connecting at the trade show booth today.</p>
                    <p className="text-gray-600">As discussed, here is our official product specification catalog and pricing guide:</p>
                    <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/80 flex items-center justify-between text-xs font-bold text-blue-600 hover:bg-blue-50/50 transition-colors cursor-pointer">
                      <span className="flex items-center gap-2"><FileText className="w-4 h-4 text-blue-500" /> Official_Product_Brochure_2026.pdf</span>
                      <span className="text-[10px] text-gray-400 font-normal">↓ 2.4 MB</span>
                    </div>
                    <div className="flex justify-end items-center gap-1 text-[10px] text-gray-400 font-semibold pt-1">
                      <span>3:01 PM</span>
                      <span className="text-blue-500 font-bold">✓✓</span>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between pl-0 sm:pl-20 text-sm font-bold text-gray-500">
                  <span className="flex items-center gap-2 text-gray-700"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Verified for High-Density Exhibitions</span>
                  <button onClick={() => navigate("/auth/register")} className="hidden sm:inline-flex items-center gap-1 font-bold text-emerald-600 hover:underline">Try Feature &rarr;</button>
                </div>
              </div>

              {/* CARD 04: AUTOMATED EMAIL FOLLOW-UPS */}
              <div 
                id="feature-4"
                onMouseEnter={() => setActiveStep(4)}
                className={`p-8 sm:p-10 rounded-[2.5rem] bg-white border transition-all duration-300 shadow-sm hover:shadow-xl relative overflow-hidden group ${
                  activeStep === 4 ? 'border-purple-300 ring-4 ring-purple-500/5 shadow-lg' : 'border-gray-200/80'
                }`}
              >
                <div className="absolute top-0 left-0 right-0 h-2 bg-purple-600"></div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-purple-600 text-white font-black text-2xl flex items-center justify-center shadow-lg transform group-hover:scale-105 transition-transform flex-shrink-0">04</div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-purple-50 text-purple-600 border border-purple-200">Keep Your Brand Top of Mind</span>
                    <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mt-2">Automated Email Follow-ups</h2>
                  </div>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed pl-0 sm:pl-20">
                  Schedule automated email campaigns at intervals you choose to re-engage exhibition visitors. Your sales administrators are CC'd on every message to ensure seamless conversation handoffs.
                </p>

                {/* LIVE INTERACTIVE WIDGET 4: Campaign Timeline */}
                <div className="mt-6 sm:ml-20 p-5 rounded-2xl bg-purple-50/70 border border-purple-100/80">
                  <div className="flex items-center justify-between text-xs font-bold text-purple-900 mb-3">
                    <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-purple-600" /> Automated Campaign Sequence</span>
                    <span className="text-[11px] text-purple-600 font-semibold">CC: Sales Admin Team</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="p-3 rounded-xl bg-white border border-purple-200/80 shadow-sm">
                      <span className="block text-[10px] font-bold text-purple-600 uppercase mb-0.5">Day 1 (Instant)</span>
                      <span className="font-bold text-gray-800">Booth Thank You</span>
                    </div>
                    <div className="p-3 rounded-xl bg-white border border-purple-200/80 shadow-sm">
                      <span className="block text-[10px] font-bold text-purple-600 uppercase mb-0.5">Day 3</span>
                      <span className="font-bold text-gray-800">Video Demo Link</span>
                    </div>
                    <div className="p-3 rounded-xl bg-purple-600 text-white shadow-md font-bold transform scale-105">
                      <span className="block text-[10px] text-purple-200 uppercase mb-0.5">Day 7</span>
                      <span>Meeting Request</span>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between pl-0 sm:pl-20 text-sm font-bold text-gray-500">
                  <span className="flex items-center gap-2 text-gray-700"><CheckCircle2 className="w-5 h-5 text-purple-600" /> Verified for High-Density Exhibitions</span>
                  <button onClick={() => navigate("/auth/register")} className="hidden sm:inline-flex items-center gap-1 font-bold text-purple-600 hover:underline">Try Feature &rarr;</button>
                </div>
              </div>

              {/* CARD 05: ONE-CLICK EXPORT */}
              <div 
                id="feature-5"
                onMouseEnter={() => setActiveStep(5)}
                className={`p-8 sm:p-10 rounded-[2.5rem] bg-white border transition-all duration-300 shadow-sm hover:shadow-xl relative overflow-hidden group ${
                  activeStep === 5 ? 'border-amber-300 ring-4 ring-amber-500/5 shadow-lg' : 'border-gray-200/80'
                }`}
              >
                <div className="absolute top-0 left-0 right-0 h-2 bg-amber-600"></div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-amber-600 text-white font-black text-2xl flex items-center justify-center shadow-lg transform group-hover:scale-105 transition-transform flex-shrink-0">05</div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-amber-50 text-amber-600 border border-amber-200">Easy Data Portability</span>
                    <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mt-2">One-Click Export & CRM Sync</h2>
                  </div>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed pl-0 sm:pl-20">
                  View all captured leads, visit locations, and tags in one unified cloud dashboard. Download your reports instantly as Excel/PDF files, or share lead lists directly with your team via WhatsApp.
                </p>

                {/* LIVE INTERACTIVE WIDGET 5: Clickable Format Exporter */}
                <div className="mt-6 sm:ml-20 p-5 rounded-2xl bg-amber-50/70 border border-amber-100/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-amber-900 block">Select export format to preview:</span>
                    <span className="text-xs text-amber-700 mt-0.5 block">Ready for Salesforce, HubSpot & Zoho</span>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto justify-end">
                    {['.XLSX', '.PDF', '.CSV'].map((ext, idx) => {
                      const isSelected = exportFormat === ext;
                      return (
                        <button 
                          key={idx} 
                          onClick={() => setExportFormat(ext)}
                          className={`flex-1 sm:flex-initial px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 active:scale-95 ${
                            isSelected 
                              ? 'bg-amber-600 text-white shadow-md shadow-amber-600/20 scale-105' 
                              : 'bg-white text-gray-700 border border-gray-200/80 hover:border-amber-300'
                          }`}
                        >
                          <FileSpreadsheet className="w-3.5 h-3.5" />
                          {ext}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between pl-0 sm:pl-20 text-sm font-bold text-gray-500">
                  <span className="flex items-center gap-2 text-gray-700"><CheckCircle2 className="w-5 h-5 text-amber-600" /> Verified for High-Density Exhibitions</span>
                  <button onClick={() => navigate("/auth/register")} className="hidden sm:inline-flex items-center gap-1 font-bold text-amber-600 hover:underline">Try Feature &rarr;</button>
                </div>
              </div>

            </div>

          </div>
        </div>

        {/* 4. BOTTOM CALL TO ACTION BANNER */}
        <div className="max-w-6xl mx-auto px-6 mt-24">
          <div className="p-10 sm:p-14 rounded-[3rem] bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white shadow-2xl shadow-indigo-500/25 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-80 h-80 bg-white/10 rounded-full blur-3xl pointer-events-none"></div>
            
            <h2 className="text-3xl sm:text-5xl font-black uppercase tracking-tight mb-4">
              Ready to upgrade your booth?
            </h2>
            <p className="text-blue-100 text-lg max-w-xl mx-auto mb-8">
              Join hundreds of B2B sales teams who have eliminated manual data entry. Start scans.
            </p>
            
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button 
                onClick={() => navigate("/auth/register")}
                className="px-8 py-4 bg-white text-gray-900 font-black rounded-2xl shadow-xl hover:bg-blue-50 hover:scale-105 active:scale-95 transition-all text-lg"
              >
                Get Started Now
              </button>
              <Link 
                to="/leads" 
                className="px-8 py-4 bg-white/10 text-white border border-white/20 font-bold rounded-2xl backdrop-blur-md hover:bg-white/20 transition-all text-lg"
              >
                Speak to Sales
              </Link>
            </div>
          </div>
        </div>

      </main>

      {/* 5. CLEAN FOOTER */}
      <footer className="py-8 bg-white border-t border-gray-200 mt-auto">
        <div className="flex flex-col items-center justify-between px-6 mx-auto max-w-7xl md:flex-row">
          <p className="text-sm font-medium text-gray-500">
            Copyright &copy; {new Date().getFullYear()} NS Engineers. All rights reserved.
          </p>
        </div>
      </footer>

    </div>
  );
};

export default Features;